import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CreditCard,
  FileText,
  Key,
  Shield,
  Settings,
  Languages,
} from "lucide-react";
import { Button } from "@core/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";

interface AccountPageTemplateProps {
  title: string;
  description: string;
  testId: string;
  children?: React.ReactNode;
}

const accountLinks = [
  {
    labelKey: "account.nav.subscription",
    to: "/account/subscription",
    icon: CreditCard,
  },
  { labelKey: "account.nav.invoices", to: "/account/invoices", icon: FileText },
  { labelKey: "account.nav.security", to: "/account/security", icon: Shield },
  {
    labelKey: "account.nav.preferences",
    to: "/account/preferences",
    icon: Languages,
  },
  { labelKey: "account.nav.api_keys", to: "/account/api-keys", icon: Key },
  {
    labelKey: "account.nav.data_management",
    to: "/data-control",
    icon: Settings,
  },
];

export default function AccountPageTemplate({
  title,
  description,
  testId,
  children,
}: AccountPageTemplateProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  return (
    <div
      className="min-h-screen-safe bg-gray-950 p-4 text-white sm:p-6 lg:p-8"
      data-testid={testId}
    >
      <div className="mx-auto max-w-5xl">
        <Button
          variant="ghost"
          className="mb-4 text-gray-300 hover:text-white"
          onClick={() => navigate(-1)}
          data-testid={`${testId}-back`}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("account.back")}
        </Button>

        <h1 className="mb-2 text-3xl font-bold" data-testid={`${testId}-title`}>
          {title}
        </h1>
        <p className="mb-6 text-gray-300" data-testid={`${testId}-description`}>
          {description}
        </p>

        <div className="grid gap-4 md:grid-cols-[220px_1fr]">
          <Card className="h-fit border-gray-800 bg-gray-900/80">
            <CardHeader>
              <CardTitle className="text-sm text-gray-200">
                {t("account.nav.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {accountLinks.map((link) => (
                <Button
                  key={link.to}
                  asChild
                  variant="ghost"
                  className="w-full justify-start text-gray-300 hover:text-white"
                >
                  <Link to={link.to}>
                    <link.icon className="mr-2 h-4 w-4" />
                    {t(link.labelKey)}
                  </Link>
                </Button>
              ))}
            </CardContent>
          </Card>

          {children || (
            <Card className="border-gray-800 bg-gray-900/80">
              <CardHeader>
                <CardTitle className="text-white">
                  {t("account.coming_soon.title")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-gray-300">
                <p>{t("account.coming_soon.intro")}</p>
                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-400">
                  <li>{t("account.coming_soon.item_plan")}</li>
                  <li>{t("account.coming_soon.item_invoices")}</li>
                  <li>{t("account.coming_soon.item_account")}</li>
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
